import { FC, useEffect } from "react";
import { Button } from "@capacit-com/lara-law-shared-ui";
import { Input } from "@capacit-com/lara-law-shared-ui";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormDialog,
  FormDialogCancel,
} from "@capacit-com/lara-law-shared-ui";
import { FolderIcon, PlusIcon, Sparkles } from "lucide-react";
import { Separator } from "@capacit-com/lara-law-shared-ui";
import * as z from "zod";
import { useForm } from "react-hook-form";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@capacit-com/lara-law-shared-ui";
import { Spinner } from "@capacit-com/lara-law-shared-ui";

const formSchema = z.object({
  name: z
    .string()
    .min(1, { message: "Udfyld navn" })
    .max(50, { message: "Navn må højst være 50 tegn" }),
});

type FormValues = z.infer<typeof formSchema>;

type props = {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  onCreateFolder: (name: string) => void;
  isPending?: boolean;
};

const CreateFolderDialog: FC<props> = ({
  isOpen,
  setIsOpen,
  onCreateFolder,
  isPending,
}) => {
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
    },
  });

  // The dialog stays mounted between opens, so without this the last submitted
  // name is still in the field the next time it opens (LAR-657).
  useEffect(() => {
    if (isOpen) form.reset({ name: "" });
  }, [isOpen, form]);

  return (
    <FormDialog
      open={isOpen}
      onOpenChange={setIsOpen}
      isDirty={form.formState.isDirty}
      size="md"
    >
      <DialogHeader className="space-y-3">
        <div className="flex items-center gap-2">
          <div className="bg-primary/10 p-2 rounded-full">
            <FolderIcon className="h-5 w-5 text-primary" />
          </div>
          <DialogTitle className="text-xl">Opret Mappe</DialogTitle>
        </div>
        <DialogDescription className="text-sm text-muted-foreground">
          Opret en ny mappe til at organisere dine dokumenter og filer.
        </DialogDescription>
      </DialogHeader>

      <Separator className="my-1" />

      <Form {...form}>
        <form
          onSubmit={form.handleSubmit((data) => onCreateFolder(data.name))}
          className="space-y-6"
        >
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="flex items-center gap-1.5">
                  <Sparkles className="h-3.5 w-3.5 text-primary" />
                  Mappe navn
                </FormLabel>
                <div className="relative">
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="F.eks. NDAer"
                      className="pl-9"
                      data-testid="name"
                    />
                  </FormControl>
                  <FolderIcon className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                </div>
                <FormMessage />
              </FormItem>
            )}
          />

          <DialogFooter className="gap-2 space-x-2 sm:gap-0 mt-6">
            <FormDialogCancel />
            <Button
              type="submit"
              className="gap-2 min-w-[100px]"
              disabled={isPending}
            >
              {isPending ? (
                <Spinner className="h-4 w-4 text-primary-foreground" />
              ) : (
                <span className="flex items-center gap-2">
                  <PlusIcon className="h-4 w-4" />
                  Opret
                </span>
              )}
            </Button>
          </DialogFooter>
        </form>
      </Form>
    </FormDialog>
  );
};

export default CreateFolderDialog;
